import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import BookClubShareSection from "./BookClubShareSection";
import BookClubDutySection from "./BookClubDutySection";
import BookClubGratitudeBoard from "./BookClubGratitudeBoard";
import EbookLibrarySection from "../components/ebook/EbookLibrarySection";
import { apiFetch, assetUrl } from "../utils/api";

function formatDateRange(start, end) {
  if (start && end) return `${start} 至 ${end}`;
  if (start) return `自 ${start} 起`;
  if (end) return `至 ${end} 止`;
  return "";
}

const TAB_KEYS = new Set(["reading", "ebooks", "duty", "gratitude"]);

function resolveTab(raw) {
  return TAB_KEYS.has(raw) ? raw : "gratitude";
}

export default function BookClubPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = resolveTab(searchParams.get("tab") || "gratitude");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch("/api/public/book-club")
      .then((res) => res.json())
      .then((json) => {
        if (json.detail) throw new Error(json.detail);
        setData(json);
      })
      .catch((err) => setError(err.message || "加载失败，请确认后端已启动"))
      .finally(() => setLoading(false));
  }, []);

  const settings = data?.settings;
  const current = data?.current;
  const readings = data?.readings || [];
  const pastAndOther = readings.filter((item) => !current || item.id !== current.id);

  useSeo(
    settings
      ? {
          title:
            tab === "ebooks"
              ? `${settings.page_title}_电子书 - AI婚恋平台`
              : tab === "duty"
                ? `${settings.page_title}_值日表 - AI婚恋平台`
                : tab === "gratitude"
                  ? `${settings.page_title}_感恩留言板 - AI婚恋平台`
                  : settings.meta_title,
          metaDescription:
            tab === "ebooks"
              ? "读书会电子书：精选婚恋、心理、沟通、家庭成长读物，会员免费下载。"
              : tab === "duty"
                ? "读书会值日表：填写每日主持、唱歌、放映与读书计划。"
                : tab === "gratitude"
                  ? "感恩留言板：账号登录并完善姓名与头像后，每天可写三到五件感恩的事。"
                  : settings.meta_description,
          ogDescription:
            tab === "ebooks"
              ? "读书会内的会员电子书，免费下载精选读物。"
              : tab === "duty"
                ? "周一至周五值日安排，会员可在线填写。"
                : tab === "gratitude"
                  ? "读书会感恩留言板，记录每日感恩。"
                  : settings.page_subtitle || "晨读读书会，好书共读。",
          keywords:
            tab === "ebooks"
              ? "读书会,电子书,婚恋阅读,心理成长"
              : tab === "duty"
                ? "读书会,值日表,晨读,主持,唱歌"
                : tab === "gratitude"
                  ? "读书会,感恩留言板,感恩月,打卡"
                  : settings.meta_keywords,
          path:
            tab === "ebooks"
              ? "/book-club?tab=ebooks"
              : tab === "duty"
                ? "/book-club?tab=duty"
                : tab === "gratitude"
                  ? "/book-club?tab=gratitude"
                  : "/book-club",
          noindex: tab === "gratitude",
        }
      : { title: "晨读读书会", path: "/book-club", noindex: true }
  );

  const switchTab = (nextTab) => {
    if (nextTab === "ai") {
      navigate("/book-club/ai-center");
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (nextTab === "ebooks") {
      next.set("tab", "ebooks");
    } else if (nextTab === "duty") {
      next.set("tab", "duty");
      next.delete("category_id");
      next.delete("keyword");
    } else if (nextTab === "reading") {
      next.set("tab", "reading");
      next.delete("category_id");
      next.delete("keyword");
    } else if (nextTab === "gratitude") {
      // 默认 Tab：打开 /book-club 即感恩留言板
      next.delete("tab");
      next.delete("category_id");
      next.delete("keyword");
    } else {
      next.delete("tab");
      next.delete("category_id");
      next.delete("keyword");
    }
    setSearchParams(next);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-pink-50 to-purple-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-gray-400">加载中...</div>
        <Footer />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-pink-50 to-purple-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-gray-500">{error || "暂无内容"}</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-pink-50 to-purple-50 flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-10">
        <div
          className={`mx-auto space-y-8 ${
            tab === "ebooks" || tab === "duty" || tab === "gratitude"
              ? "max-w-6xl"
              : "max-w-4xl"
          }`}
        >
          <header className="text-center space-y-4">
            <p className="text-sm text-amber-600 font-medium tracking-wide">会员阅读</p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">{settings.page_title}</h1>
            <p className="text-gray-600">{settings.page_subtitle}</p>
            {tab === "reading" || tab === "duty" ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-amber-100 text-sm text-gray-700">
                <span className="text-amber-500">🕖</span>
                {settings.schedule_label}
              </div>
            ) : null}
            {tab === "reading" ? (
              <div className="flex justify-center gap-3 flex-wrap">
                <Link
                  to="/reading/checkin"
                  className="inline-flex px-5 py-2.5 rounded-full bg-stone-800 text-white text-sm font-medium"
                >
                  今日分享
                </Link>
                <Link
                  to="/growth"
                  className="inline-flex px-5 py-2.5 rounded-full border border-stone-300 text-sm font-medium"
                >
                  公开的读书整理
                </Link>
                <Link
                  to="/book-club/city-apply"
                  className="inline-flex px-5 py-2.5 rounded-full border border-rose-300 text-rose-700 text-sm font-medium bg-white/80"
                >
                  城市线下招募
                </Link>
                <Link
                  to="/book-club/ai-center"
                  className="inline-flex px-5 py-2.5 rounded-full bg-amber-700 text-white text-sm font-medium"
                >
                  AI 共读中心
                </Link>
              </div>
            ) : null}
          </header>

          <div className="flex justify-center flex-wrap gap-2">
            {[
              ["gratitude", "感恩留言板"],
              ["reading", "晨读共读"],
              ["duty", "值日表"],
              ["ebooks", "电子书"],
              ["ai", "AI共读"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => switchTab(key)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium border transition ${
                  tab === key
                    ? "bg-pink-500 text-white border-pink-500"
                    : "bg-white text-gray-600 border-gray-200 hover:border-pink-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "ebooks" ? (
            <EbookLibrarySection />
          ) : tab === "duty" ? (
            current ? (
              <BookClubDutySection current={current} />
            ) : (
              <div className="bg-white rounded-2xl shadow p-10 text-center text-gray-400">
                暂无当期共读，值日表将随当期书目一起开放
              </div>
            )
          ) : tab === "gratitude" ? (
            <BookClubGratitudeBoard />
          ) : (
            <>
              {settings.intro_text && (
                <section className="bg-white rounded-2xl shadow p-6 md:p-8 text-gray-600 leading-relaxed">
                  {settings.intro_text}
                </section>
              )}

              {current && (
                <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-pink-100">
                  <div className="bg-gradient-to-r from-pink-500 to-rose-400 px-6 py-3 text-white text-sm font-medium">
                    当期共读 · {current.period_label}
                  </div>
                  <div className="p-6 md:p-8 space-y-4">
                    <div className="flex flex-col md:flex-row gap-6">
                      {current.cover_url ? (
                        <img
                          src={assetUrl(current.cover_url)}
                          alt={current.book_title}
                          className="w-32 h-44 object-cover rounded-lg shadow shrink-0 mx-auto md:mx-0"
                        />
                      ) : (
                        <div className="w-32 h-44 bg-gradient-to-br from-amber-100 to-pink-100 rounded-lg flex items-center justify-center text-4xl shrink-0 mx-auto md:mx-0">
                          📖
                        </div>
                      )}
                      <div className="flex-1 space-y-2 text-center md:text-left">
                        <h2 className="text-2xl font-bold text-gray-800">{current.book_title}</h2>
                        {current.book_author && (
                          <p className="text-gray-500 text-sm">作者：{current.book_author}</p>
                        )}
                        <p className="text-sm text-pink-500">
                          {current.schedule_note || settings.schedule_label}
                        </p>
                        {formatDateRange(current.start_date, current.end_date) && (
                          <p className="text-xs text-gray-400">
                            期次：{formatDateRange(current.start_date, current.end_date)}
                          </p>
                        )}
                      </div>
                    </div>
                    {current.summary && (
                      <div className="rounded-xl bg-rose-50 p-4 text-sm text-gray-600 leading-relaxed">
                        <h3 className="font-semibold text-gray-800 mb-2">内容简介</h3>
                        <p className="whitespace-pre-line">{current.summary}</p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {current && <BookClubShareSection current={current} />}

              {pastAndOther.length > 0 && (
                <section className="space-y-4">
                  <h2 className="text-xl font-bold text-gray-800">更多期次</h2>
                  <div className="space-y-3">
                    {pastAndOther.map((item) => (
                      <article
                        key={item.id}
                        className="bg-white rounded-xl shadow p-5 border border-transparent hover:border-pink-100 transition"
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                          <span className="text-xs text-pink-500 font-medium">{item.period_label}</span>
                          <span className="text-xs text-gray-400">
                            {item.schedule_note || settings.schedule_label}
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-800">{item.book_title}</h3>
                        {item.book_author && (
                          <p className="text-sm text-gray-500 mt-1">作者：{item.book_author}</p>
                        )}
                        {item.summary && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-3 whitespace-pre-line">
                            {item.summary}
                          </p>
                        )}
                        {formatDateRange(item.start_date, item.end_date) && (
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDateRange(item.start_date, item.end_date)}
                          </p>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              )}

              <section className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-2xl border border-rose-100 p-6 md:p-8 space-y-3">
                <p className="text-sm text-rose-600 font-medium">线下城市读书会</p>
                <h2 className="text-lg font-bold text-gray-800">在你的城市发起共读</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  与线上读同一本书，组织轻量稳定的线下相聚。登录会员可提交合作申请，我们评估后与你联系。
                </p>
                <Link
                  to="/book-club/city-apply"
                  className="inline-flex px-6 py-3 bg-rose-600 text-white rounded-full text-sm font-medium hover:bg-rose-700 transition"
                >
                  填写招募申请
                </Link>
              </section>

              <section className="bg-white rounded-2xl shadow p-6 md:p-8 space-y-4">
                <h2 className="text-lg font-bold text-gray-800">如何参与</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {settings.join_note || "注册会员后，请联系平台咨询师了解当期晨读安排。"}
                </p>
                <p className="text-gray-500 text-sm">
                  可切换上方「值日表」填写每日岗位，或到「电子书」下载精选读物。
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => switchTab("duty")}
                    className="px-6 py-3 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition"
                  >
                    去填值日表
                  </button>
                  <button
                    type="button"
                    onClick={() => switchTab("ebooks")}
                    className="px-6 py-3 bg-indigo-500 text-white rounded-full text-sm font-medium hover:bg-indigo-600 transition"
                  >
                    去看看电子书
                  </button>
                  <Link
                    to="/register"
                    className="px-6 py-3 bg-pink-500 text-white rounded-full text-sm font-medium hover:bg-pink-600 transition"
                  >
                    注册会员
                  </Link>
                  <Link
                    to="/consultants"
                    className="px-6 py-3 border border-pink-400 text-pink-600 rounded-full text-sm hover:bg-pink-50 transition"
                  >
                    联系咨询师
                  </Link>
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
